import React, { useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogTitle,
    makeStyles,
    DialogContent,
    DialogActions,
    Grid,
    CircularProgress,
    Radio,
    Typography,
    Card,
    ListItem,
    CardContent,
    TextField,
    InputAdornment,
    AccordionDetails,
    Accordion,
    AccordionSummary,
    Divider,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { Form } from 'react-final-form'
import { NoData } from '../../../shared/components/NoData'
import { UserModel } from '../../../shared/types/user'
import { useTranslation } from 'react-i18next'
import { ChargerConnectorType } from '../../../shared/types/charger'
import SearchIcon from '@material-ui/icons/Search'
import { Day, TariffRowModel } from '../../../shared/types/tariff'
import { PricingConditionModel } from '../../../shared/types/pricingCondition'
import { fetchTags } from '../../../redux/actions/tags'
import { fetchTariffs } from '../../../redux/actions/tariffs'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { fetchPricingConditionsForConnector } from '../../../redux/actions/locations'

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
    },
    form: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    search: {
        marginBottom: theme.spacing(2),
    },
    list: {
        maxHeight: 300,
        overflowY: 'auto',
    },
    actions: {
        padding: theme.spacing(2),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    listContentWrapper: {
        // height: '260px',
        overflow: 'auto',
        padding: '0px !important',
    },
    progress: {
        textAlign: 'center',
        position: 'relative',
        top: '5px',
    },
    dataInfoWrapper: {
        textAlign: 'center',
        paddingLeft: theme.spacing(1),
    },
    checkbox: {
        color: '#6100ED',
        marginBottom: '4px',
        paddingLeft: '0px',
        paddingBottom: '0px',
        paddingTop: '0px',
        paddingRight: '8px',
    },
    content: {
        overflow: 'hidden',
    },
    input: {
        backgroundColor: '#f5f5f5',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5',
            borderRadius: theme.shape.borderRadius,
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none',
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px',
        },
        '& .MuiInputLabel-root': {
            top: '-7px',
            fontSize: '0.9rem',
            // color: '#9e9e9e',
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    },
    profileItem: {
        textAlign: 'left',
    },
    profileValue: {
        paddingLeft: theme.spacing(1),
        textAlign: 'left',
        position: 'relative',
        width: '80%',
        '& span': {
            position: 'absolute',
            top: theme.spacing(0.3),
        },
    },
    accordionTitle: {
        width: '302px',
    },
    sectionHeader: {
        marginBottom: '10px',
    },
    scheduleNote: {
        marginBottom: '10px',
    },
    accordionRadio: {
        padding: '0px !important',
    },
    accordionRadioWrapper: {
        textAlign: 'right',
    },
    dividerMarginBottom: {
        marginBottom: theme.spacing(2),
    },
}))

interface StartTransactionDetails {
    userId: string
    pricingConditionId: string
    connectorId: string
}

interface StartTransactionDialogProps {
    open: boolean
    onClose(startTransactionDetails: StartTransactionDetails): void
    connectorId: string
}

const StartTransactionDialog: React.FC<StartTransactionDialogProps> = ({ open, onClose, connectorId }) => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const usersExecuted = useSelector((state: any) => state.users.items.executed)
    const users = useSelector((state: any) => state.users.items.value)
    const [search, setSearch] = useState('')
    const [selectedUser, setSelectedUser] = useState<string>('')
    const [pricingConditionId, setPricingConditionId] = useState<string | null>(null)
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const tags = useSelector((state: any) => state.tags.items.all.value)
    const tariffs = useSelector((state: any) => state.tariffs.items.value)
    const pricingConditionsState = useSelector((state: any) => state.locations.chargers.connectorPricingConditions)

    useEffect(() => {
        if (open) {
            dispatch(fetchTags())
            dispatch(fetchTariffs(tenantId))
        }
    }, [dispatch, open])

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value)
    }
    const handleSubmit = () => {
        if (selectedUser) {
            const transactionDetails: StartTransactionDetails = {
                connectorId: connectorId,
                userId: selectedUser,
                pricingConditionId: pricingConditionId || '',
            }
            onClose(transactionDetails)
        }
    }

    const filteredUsers = users.filter((user: UserModel) => {
        const searchLower = search.toLowerCase()

        const simpleMatches =
            user.firstName.toLowerCase().includes(searchLower) ||
            user.lastName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.phone?.toLowerCase().includes(searchLower)

        const rfidMatches = user.rfids?.some((rfid) => rfid.rfid.toLowerCase().includes(searchLower))
        const carMatches = user.cars?.some((car) => car.carLicensePlate.toLowerCase().includes(searchLower))
        
        return simpleMatches || rfidMatches || carMatches
    })

    useEffect(() => {
        const fetchAndUpdate = async () => {
            try {
                if (pricingConditionsState.executed && connectorId) {
                    // Fetch data and dispatch an action to update the Redux store
                    await dispatch(fetchPricingConditionsForConnector(connectorId, selectedUser))
                }
            } catch (error) {
                console.error('Failed to fetch pricing conditions', error)
            }
        }

        if (selectedUser) {
            fetchAndUpdate()
        }
    }, [dispatch, selectedUser, connectorId])

    const handleSelectedUser = (userId: string) => {
        setSelectedUser(userId)
    }

    const handleRadioChange = (conditionId: string) => {
        setPricingConditionId((prevId) => (prevId === conditionId ? null : conditionId))
    }

    const formatDate = (dateString: Date) => {
        const date = new Date(dateString)

        const day = date.getDate()
        const month = date.toLocaleString('en-GB', { month: 'long' }) // Full month name
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')

        return `${day}. ${month} ${year}, ${hours}:${minutes}`
    }

    const getChargingUnits = (connectorType: ChargerConnectorType | null | undefined) => {
        switch (connectorType) {
            case ChargerConnectorType.Ac:
                return ' A'
            case ChargerConnectorType.Dc:
                return ' kW'
            default:
                return ''
        }
    }

    const getConnectorTypeName = (type: ChargerConnectorType | null | undefined): string => {
        switch (type) {
            case ChargerConnectorType.Ac:
                return 'AC'
            case ChargerConnectorType.Dc:
                return 'DC'
            default:
                return ''
        }
    }

    return (
        <Dialog
            open={open}
            onClose={() => onClose({ userId: '', pricingConditionId: '', connectorId: '' })}
            className={classes.dialog}
        >
            <DialogTitle>Select user and pricing condition</DialogTitle>
            <Form
                onSubmit={handleSubmit}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <DialogContent className={classes.content}>
                            <Grid container alignItems="flex-start" spacing={2}>
                                <Grid item xs={10} className={classes.search}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        placeholder={t('users.search')}
                                        value={search}
                                        onChange={handleSearchChanged}
                                        className={classes.input}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2} className={classes.progress}>
                                    {!usersExecuted ||
                                        (!pricingConditionsState.executed && <CircularProgress size={30} />)}
                                </Grid>
                                <Grid item xs={12} className={classes.listContentWrapper}>
                                    {usersExecuted && filteredUsers.length === 0 ? (
                                        <div className={classes.dataInfoWrapper}>
                                            <NoData text={t('users.noUsers')} />
                                        </div>
                                    ) : (
                                        filteredUsers.map((user: UserModel, index: number) => (
                                            <Card key={user.id} className={classes.listItem} elevation={1}>
                                                <ListItem>
                                                    <Grid container alignItems="center">
                                                        <Grid item xs={10}>
                                                            <CardContent className={classes.listContent}>
                                                                <Typography variant="h6">{`${user.firstName} ${user.lastName}`}</Typography>
                                                                <Typography component="div" variant="caption">
                                                                    {user.email}
                                                                </Typography>
                                                            </CardContent>
                                                        </Grid>
                                                        <Grid item xs={2} style={{ textAlign: 'right' }}>
                                                            <Radio
                                                                className={classes.checkbox}
                                                                checked={selectedUser === user.id}
                                                                onChange={() => handleSelectedUser(user.id)} // Update selectedUser on click
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </ListItem>
                                            </Card>
                                        ))
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    {selectedUser &&
                                    pricingConditionsState.executed &&
                                    Array.isArray(pricingConditionsState.value) &&
                                    pricingConditionsState.value?.length !== 0 ? (
                                        <>
                                            <Typography variant="subtitle1">
                                                Select pricing condition (optional):
                                            </Typography>
                                            <Divider className={classes.dividerMarginBottom} />
                                            {pricingConditionsState.value.map((condition: PricingConditionModel) => {
                                                const pricingConditionValues = [
                                                    [
                                                        t('pricingConditions.maxSessionsPerUser'),
                                                        !condition.maxSessionsPerUser ||
                                                        condition.maxSessionsPerUser === '0'
                                                            ? 'Unrestricted'
                                                            : condition.maxSessionsPerUser,
                                                    ],
                                                    [
                                                        t('pricingConditions.connectorType'),
                                                        getConnectorTypeName(condition.connectorType),
                                                    ],
                                                    [t('pricingConditions.country'), condition.country],
                                                    [t('pricingConditions.validFrom'), formatDate(condition.validFrom)],
                                                    [t('pricingConditions.validTo'), formatDate(condition.validTo)],
                                                    [
                                                        t('pricingConditions.priceForKwh'),
                                                        !condition.priceForKwh || condition.priceForKwh === '0'
                                                            ? 'Unrestricted'
                                                            : condition.priceForKwh + ' €',
                                                    ],
                                                    [
                                                        t('pricingConditions.limitPerSession'),
                                                        !condition.limitPerSession || condition.limitPerSession === '0'
                                                            ? 'Unrestricted'
                                                            : condition.limitPerSession + ' kWh',
                                                    ],
                                                    [
                                                        t('pricingConditions.priceForMinuteOfParking'),
                                                        !condition.priceForMinuteOfParking ||
                                                        condition.priceForMinuteOfParking === '0'
                                                            ? 'Unrestricted'
                                                            : condition.priceForMinuteOfParking + ' €',
                                                    ],
                                                    [
                                                        t('pricingConditions.amountOfFreeParking'),
                                                        !condition.amountOfFreeParking ||
                                                        condition.amountOfFreeParking === '0'
                                                            ? 'Unrestricted'
                                                            : condition.amountOfFreeParking + ' min',
                                                    ],
                                                    [
                                                        t('pricingConditions.maxSpeedOfCharging'),
                                                        !condition.maxSpeedOfCharging ||
                                                        condition.maxSpeedOfCharging === '0'
                                                            ? 'Unrestricted'
                                                            : condition.maxSpeedOfCharging +
                                                              getChargingUnits(condition.connectorType),
                                                    ],
                                                    [
                                                        t('pricingConditions.maximumDurationOfCharging'),
                                                        !condition.maximumDurationOfCharging ||
                                                        condition.maximumDurationOfCharging === '0'
                                                            ? 'Unrestricted'
                                                            : condition.maximumDurationOfCharging + ' min',
                                                    ],
                                                    [
                                                        t('pricingConditions.userTag'),
                                                        tags?.find((tag: any) => tag.id === condition.userTag)?.name,
                                                    ],
                                                    [
                                                        t('pricingConditions.chargerTag'),
                                                        tags?.find((tag: any) => tag.id === condition.chargerTag)?.name,
                                                    ],
                                                    [
                                                        t('pricingConditions.locationTag'),
                                                        tags?.find((tag: any) => tag.id === condition.locationTag)
                                                            ?.name,
                                                    ],
                                                    [
                                                        t('pricingConditions.chargingProfileTag'),
                                                        tags?.find(
                                                            (tag: any) => tag.id === condition.chargingProfileTag
                                                        )?.name,
                                                    ],
                                                ]

                                                return (
                                                    <Grid container alignItems="center" key={condition.id}>
                                                        <Grid item xs={11}>
                                                            <Accordion>
                                                                <AccordionSummary
                                                                    expandIcon={<ExpandMoreIcon />}
                                                                    aria-controls={`panel-${condition.id}-content`}
                                                                    id={`panel-${condition.id}-header`}
                                                                >
                                                                    <Grid item className={classes.accordionTitle}>
                                                                        <Typography variant="body1">
                                                                            {condition.name}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item>
                                                                        <Typography variant="caption">
                                                                            {t('pricingConditions.priority')}:{' '}
                                                                            {condition.priority}
                                                                        </Typography>
                                                                    </Grid>
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Grid container spacing={2}>
                                                                        {condition.description && (
                                                                            <Grid item xs={12}>
                                                                                <Typography variant="body2">
                                                                                    {condition.description}
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                        <Grid item xs={12}>
                                                                            {pricingConditionValues.map((x, index) =>
                                                                                x.length === 2 && x[1] ? (
                                                                                    <Grid container key={index}>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={7}
                                                                                            className={
                                                                                                classes.profileItem
                                                                                            }
                                                                                        >
                                                                                            <Typography variant="body2">
                                                                                                {' '}
                                                                                                {x[0]}{' '}
                                                                                            </Typography>
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={5}
                                                                                            className={
                                                                                                classes.profileValue
                                                                                            }
                                                                                        >
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                component="div"
                                                                                            >
                                                                                                {x[1]}
                                                                                            </Typography>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                ) : null
                                                                            )}
                                                                        </Grid>
                                                                        <Grid item xs={12}>
                                                                            {condition.scheduleTariff &&
                                                                                (() => {
                                                                                    const selectedTariff =
                                                                                        tariffs?.find(
                                                                                            (tariff: any) =>
                                                                                                tariff.id ===
                                                                                                condition.scheduleTariff
                                                                                        )
                                                                                    return selectedTariff ? (
                                                                                        <div>
                                                                                            <Typography
                                                                                                variant="body1"
                                                                                                className={
                                                                                                    classes.sectionHeader
                                                                                                }
                                                                                            >
                                                                                                {t('tariffs.name')}:{' '}
                                                                                                {selectedTariff.name}
                                                                                            </Typography>
                                                                                            {selectedTariff.note && (
                                                                                                <Grid
                                                                                                    item
                                                                                                    xs={12}
                                                                                                    className={
                                                                                                        classes.scheduleNote
                                                                                                    }
                                                                                                >
                                                                                                    <Typography variant="body2">
                                                                                                        {
                                                                                                            selectedTariff.note
                                                                                                        }
                                                                                                    </Typography>
                                                                                                </Grid>
                                                                                            )}
                                                                                            {selectedTariff.rows.map(
                                                                                                (
                                                                                                    row: TariffRowModel,
                                                                                                    index: number
                                                                                                ) => (
                                                                                                    <Grid
                                                                                                        container
                                                                                                        key={index}
                                                                                                        spacing={1}
                                                                                                    >
                                                                                                        <Grid
                                                                                                            item
                                                                                                            xs={12}
                                                                                                        >
                                                                                                            <Typography variant="body2">
                                                                                                                {
                                                                                                                    Day[
                                                                                                                        row
                                                                                                                            .dayFrom
                                                                                                                    ]
                                                                                                                }{' '}
                                                                                                                {
                                                                                                                    row.from
                                                                                                                }{' '}
                                                                                                                -{' '}
                                                                                                                {
                                                                                                                    Day[
                                                                                                                        row
                                                                                                                            .dayTo
                                                                                                                    ]
                                                                                                                }{' '}
                                                                                                                {row.to}
                                                                                                            </Typography>
                                                                                                        </Grid>
                                                                                                    </Grid>
                                                                                                )
                                                                                            )}
                                                                                        </div>
                                                                                    ) : null
                                                                                })()}
                                                                        </Grid>
                                                                    </Grid>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        </Grid>
                                                        <Grid item xs={1} className={classes.accordionRadioWrapper}>
                                                            <Radio
                                                                checked={pricingConditionId === condition.id}
                                                                onChange={() => handleRadioChange(condition.id)}
                                                                name="pricing-condition-selection"
                                                                className={classes.checkbox}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )
                                            })}
                                        </>
                                    ) : (
                                        <p></p>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions className={classes.actions}>
                            <Button
                                onClick={() => onClose({ userId: '', pricingConditionId: '', connectorId: '' })}
                                color="primary"
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                color="secondary"
                                variant="contained"
                                disabled={!selectedUser}
                            >
                                Start
                            </Button>
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}

export default StartTransactionDialog
